import { SideData } from '@App/app/entities/layer/side-data.model';
import { CreationToolError } from '@App/app/shared/exceptions/creation-tool-error';
import { Injectable } from '@angular/core';
import { Mesh, Plane, Ray, Vector3 } from 'babylonjs';
import { MODEL_NAME } from 'src/app/configs/babylon.config';
import { SceneService } from '../../../scene-service/scene.service';
import {
  ADJACENT_SURFACE_SUB_POINT_COUNT,
  ORIGINAL_SURFACE_SUB_POINT_COUNT,
  RAY_ENDPOINT_SCALE,
  RAY_ORIGIN_OFFSET_DIVISOR,
} from './rectangle-tools.constants';
import { RectangleToolsUtilsService } from './rectangle-tools-utls.service';

@Injectable({
  providedIn: 'root',
})
export class RectangleToolsRaycastService {
  constructor(
    private _rectangleToolsUtilsService: RectangleToolsUtilsService,
    private _sceneService: SceneService,
  ) {}

  getOppositesPoints(points: Mesh[], normal: Vector3): Vector3[] {
    const normalLength = normal.length();
    return points
      .map((point) => {
        const ray = Ray.CreateNewFromTo(
          point.position.subtract(normal.clone().scale(normalLength / RAY_ORIGIN_OFFSET_DIVISOR)),
          point.position.subtract(normal.clone().scale(normalLength * RAY_ENDPOINT_SCALE)),
        );
        const hit = this._sceneService.scene.pickWithRay(ray, (mesh) =>
          mesh.name.includes(MODEL_NAME),
        );
        return hit?.pickedMesh && hit.pickedPoint ? hit.pickedPoint : undefined;
      })
      .filter((point): point is Vector3 => point !== undefined);
  }

  calculateSideData(normal: Vector3, centerPosition: Vector3): SideData {
    const pointA = this._rectangleToolsUtilsService.pickRay(
      centerPosition,
      this.getRayDirection(normal),
    );
    const pointB = this._rectangleToolsUtilsService.pickRay(
      centerPosition,
      this.getRayDirection(normal, true),
    );
    if (!pointA || !pointB) {
      throw new CreationToolError('Something went wrong. Try again.');
    }
    return { pointA, pointB, length: Vector3.Distance(pointB, pointA) };
  }

  calculateOppositePoints(
    originPoint: Vector3,
    direction: Vector3,
    frontBack: boolean,
    originalSurfacePlane?: Plane,
    originalSurfaceDistance?: number,
  ): [Vector3 | undefined, Vector3 | undefined] {
    const usePlaneOffset = frontBack && originalSurfacePlane && originalSurfaceDistance;
    const pointA = usePlaneOffset
      ? originPoint
          .clone()
          .subtract(originalSurfacePlane.normal.scale(originalSurfaceDistance / 2).negate())
      : this._rectangleToolsUtilsService.pickRay(originPoint, this.getRayDirection(direction));

    const pointB = this._rectangleToolsUtilsService.pickRay(
      originPoint,
      this.getRayDirection(direction, true),
    );

    return [pointA, pointB];
  }

  calculateSubPoints(
    num: number,
    originalVector: Vector3,
    distance: number,
    origin: Vector3,
  ): Vector3[] {
    const startPoint = origin.subtract(originalVector.scale(distance / 2));
    const step = distance / num;
    return Array.from({ length: num }, (_, i) =>
      startPoint.add(originalVector.normalize().scale(step * i)),
    );
  }

  findLongestDistance(
    subPoints: Vector3[],
    vector: Vector3,
    frontBack = false,
    originalSurfacePlane?: Plane,
    originalSurfaceDistance?: number,
  ): SideData {
    let result: SideData = { length: 0 };
    for (const point of subPoints) {
      const [pointA, pointB] = this.calculateOppositePoints(
        point,
        vector,
        frontBack,
        originalSurfacePlane,
        originalSurfaceDistance,
      );
      if (pointA && pointB) {
        const distance = Vector3.Distance(pointA, pointB);
        if (distance > result.length) {
          result = { pointA, pointB, length: distance };
        }
      }
    }
    return result;
  }

  refineSideLengths(
    originalSurfacePlane: Plane,
    adjacentSurfacePlane: Plane,
    centerPosition: Vector3,
    originalSurfaceData: SideData,
    originalSurfaceDistance: number,
  ): { originalSurfaceData: SideData; adjacentSurfaceData: SideData } {
    const originalSubPoints = this.calculateSubPoints(
      ORIGINAL_SURFACE_SUB_POINT_COUNT,
      originalSurfacePlane.normal.clone(),
      originalSurfaceData.length / 2,
      centerPosition,
    );
    const refinedAdjacent = this.findLongestDistance(
      originalSubPoints,
      adjacentSurfacePlane.normal,
    );

    const adjacentSubPoints = this.calculateSubPoints(
      ADJACENT_SURFACE_SUB_POINT_COUNT,
      adjacentSurfacePlane.normal,
      refinedAdjacent.length / 2,
      centerPosition,
    );
    const refinedOriginal = this.findLongestDistance(
      adjacentSubPoints,
      originalSurfacePlane.normal.clone(),
      true,
      originalSurfacePlane,
      originalSurfaceDistance,
    );

    return { originalSurfaceData: refinedOriginal, adjacentSurfaceData: refinedAdjacent };
  }

  private getRayDirection(direction: Vector3, negate = false): Vector3 {
    const normalized = direction.clone().normalize();
    return negate ? normalized.negate() : normalized;
  }
}
